export default {
  title: 'Terms of Service',
  lastUpdated: 'Last Updated: November 27, 2025',
  section1: {
    title: '1. Acceptance of Terms',
    content1: 'Welcome to UmiGo. By accessing or using our mobile application, website, or any related services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.',
    content2: 'These Terms constitute a legally binding agreement between you and UmiGo ("we," "us," or "our"). We reserve the right to modify these Terms at any time, and your continued use of the Services constitutes acceptance of any changes.'
  },
  section2: {
    title: '2. Eligibility',
    intro: 'You must be at least 13 years old (or 16 in certain jurisdictions) to use UmiGo. By using our Services, you represent and warrant that:',
    items: [
      'You meet the minimum age requirement',
      'You have the legal capacity to enter into these Terms',
      'You will comply with all applicable laws and regulations',
      'You have not been previously banned from using our Services',
      'All information you provide is accurate and truthful'
    ]
  },
  section3: {
    title: '3. Account Registration and Security',
    intro: 'To access certain features, you must create an account. You agree to:',
    items: [
      'Provide accurate, current, and complete information during registration',
      'Maintain and promptly update your account information',
      'Keep your password secure and confidential',
      'Notify us immediately of any unauthorized access or security breach',
      'Accept responsibility for all activities under your account',
      'Not share your account with others or create multiple accounts'
    ],
    note: 'We reserve the right to suspend or terminate accounts that violate these Terms or engage in suspicious activity.'
  },
  section4: {
    title: '4. User Conduct and Prohibited Activities',
    intro: 'You agree not to engage in any of the following prohibited activities:',
    items: [
      'Harassing, threatening, intimidating, or bullying other users',
      'Posting or sharing illegal, harmful, offensive, or inappropriate content',
      'Impersonating any person or entity or misrepresenting your affiliation',
      'Sharing sexually explicit content involving minors',
      'Promoting violence, terrorism, or hate speech',
      'Spamming, phishing, or engaging in fraudulent activities',
      'Violating intellectual property rights of others',
      'Attempting to hack, disrupt, or compromise our Services',
      'Using automated systems (bots) without authorization',
      'Collecting user information without consent',
      'Selling or transferring your account',
      'Engaging in commercial activities without our permission'
    ]
  },
  section5: {
    title: '5. Content and Intellectual Property',
    sub1: {
      title: '5.1 Your Content',
      content: 'You retain ownership of content you create and share on UmiGo ("User Content"). By posting User Content, you grant us a worldwide, non-exclusive, royalty-free, transferable license to use, reproduce, distribute, modify, adapt, publicly display, and publicly perform your User Content in connection with operating and providing the Services.'
    },
    sub2: {
      title: '5.2 Our Content',
      content: 'All content, features, and functionality of the Services, including but not limited to text, graphics, logos, icons, images, audio clips, video clips, and software, are owned by UmiGo or our licensors and are protected by copyright, trademark, and other intellectual property laws.'
    },
    sub3: {
      title: '5.3 Content Moderation',
      content: 'We reserve the right to review, monitor, and remove User Content that violates these Terms or is otherwise objectionable, but we are not obligated to do so. We may use automated systems and human moderators to enforce our content policies.'
    }
  },
  section6: {
    title: '6. Virtual Items and Purchases',
    intro: 'UmiGo may offer virtual items, gifts, or premium features for purchase ("Virtual Items"). You acknowledge that:',
    items: [
      'Virtual Items have no real-world value and cannot be exchanged for cash',
      'All purchases are final and non-refundable, except as required by law',
      'We may modify, suspend, or discontinue Virtual Items at any time',
      'Virtual Items are forfeited if your account is terminated',
      'You are responsible for all charges incurred under your account',
      'Prices are subject to change without notice'
    ]
  },
  section7: {
    title: '7. Privacy and Data Protection',
    content: 'Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information. By using our Services, you consent to our data practices as described in the Privacy Policy. Please review it carefully.'
  },
  section8: {
    title: '8. Communications and Notifications',
    intro: 'By creating an account, you consent to receive communications from us, including:',
    items: [
      'Service-related announcements and updates',
      'Security alerts and account notifications',
      'Marketing communications (which you can opt out of)',
      'Customer support responses'
    ],
    note: 'You can manage your communication preferences in your account settings.'
  },
  section9: {
    title: '9. Third-Party Services and Links',
    content: 'Our Services may contain links to third-party websites, applications, or services. We are not responsible for the content, privacy practices, or terms of service of any third parties. Your interactions with third parties are solely between you and them.'
  },
  section10: {
    title: '10. Disclaimers and Limitations of Liability',
    sub1: {
      title: '10.1 Service Availability',
      content: 'The Services are provided "as is" and "as available" without warranties of any kind. We do not guarantee that the Services will be uninterrupted, secure, or error-free. We may modify, suspend, or discontinue any part of the Services at any time without notice.'
    },
    sub2: {
      title: '10.2 Limitation of Liability',
      intro: 'To the maximum extent permitted by law, UmiGo and its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:',
      items: [
        'Loss of profits, data, or goodwill',
        'Service interruption or system failure',
        'Cost of substitute services',
        'Any damages arising from your use or inability to use the Services'
      ],
      note: 'Our total liability shall not exceed the amount you paid us in the twelve months preceding the claim, or $100, whichever is greater.'
    }
  },
  section11: {
    title: '11. Indemnification',
    intro: 'You agree to indemnify, defend, and hold harmless UmiGo and its affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:',
    items: [
      'Your use of the Services',
      'Your violation of these Terms',
      'Your violation of any rights of another party',
      'Your User Content'
    ]
  },
  section12: {
    title: '12. Termination',
    intro: 'We may suspend or terminate your account and access to the Services at any time, with or without notice, for any reason, including if:',
    items: [
      'You violate these Terms',
      'Your account has been inactive for an extended period',
      'We are required to do so by law',
      'We decide to discontinue the Services'
    ],
    note: 'You may delete your account at any time through the app settings. Upon termination, your right to use the Services will immediately cease, and we may delete your data in accordance with our Privacy Policy.'
  },
  section13: {
    title: '13. Dispute Resolution and Arbitration',
    sub1: {
      title: '13.1 Informal Resolution',
      content: 'If you have a dispute with us, please contact us first at support@umigo.cc to attempt to resolve it informally.'
    },
    sub2: {
      title: '13.2 Binding Arbitration',
      content: 'If we cannot resolve the dispute informally, any dispute arising from these Terms or the Services shall be resolved through binding arbitration, except where prohibited by law. You agree to waive your right to a jury trial or to participate in a class action lawsuit.'
    },
    sub3: {
      title: '13.3 Exceptions',
      content: 'Either party may seek injunctive relief in court for intellectual property infringement or unauthorized access to the Services.'
    }
  },
  section14: {
    title: '14. Governing Law and Jurisdiction',
    content: 'These Terms shall be governed by and construed in accordance with the laws of Mexico, without regard to its conflict of law provisions. Any legal action or proceeding shall be brought exclusively in the courts located in Mexico City, Mexico.'
  },
  section15: {
    title: '15. Changes to Terms',
    content: 'We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website or app, and updating the "Last Updated" date. Your continued use of the Services after changes become effective constitutes acceptance of the modified Terms.'
  },
  section16: {
    title: '16. Miscellaneous',
    sub1: {
      title: '16.1 Entire Agreement',
      content: 'These Terms, together with our Privacy Policy, constitute the entire agreement between you and UmiGo regarding the Services.'
    },
    sub2: {
      title: '16.2 Severability',
      content: 'If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.'
    },
    sub3: {
      title: '16.3 Waiver',
      content: 'Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.'
    },
    sub4: {
      title: '16.4 Assignment',
      content: 'You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.'
    }
  },
  section17: {
    title: '17. Contact Information',
    intro: 'If you have any questions about these Terms, please contact us:',
    email: '<strong>Email:</strong> support@umigo.cc',
    phone: '<strong>Phone:</strong> WA: +52 8 132724843',
    address: '<strong>Address:</strong> Av. Paseo de la Reforma 222, Ciudad de México, CDMX 06600, Mexico'
  },
  community: {
    title: 'Community Guidelines',
    intro: 'In addition to these Terms, all users must follow our Community Guidelines:',
    items: [
      '<strong>Be Respectful:</strong> Treat others with kindness and respect',
      '<strong>Stay Safe:</strong> Protect your personal information and report suspicious behavior',
      '<strong>Be Authentic:</strong> Use your real identity and don\'t impersonate others',
      '<strong>Keep it Legal:</strong> Don\'t engage in illegal activities or promote them',
      '<strong>Respect Privacy:</strong> Don\'t share others\' private information without consent',
      '<strong>Report Issues:</strong> Help us maintain a safe community by reporting violations'
    ]
  }
};
